// SiteManagement.js — Admin ▸ Site management
//
// Administration of stockcontrol.organisations. Visible only to the
// "Pharmace all" organisation (gated in App.js AND again on the server).
// Renders in the normal content area like every other view, so the left nav
// rail stays put.
//
// Layout
//   LEFT   — search box + alphabetical grid of every organisation.
//            Row colour: green  = has username, password and ≥1 member
//                        red    = one of those is missing
//            Each row carries a "Send email" button on the far right which
//            mails the platform access details to that org's username.
//   CENTRE — the selected organisation's members, with Add / Delete.
//   RIGHT  — what that organisation's own user would see under Settings:
//            top half  = Organisation Settings (identity, login, group
//                        manager, scheduled reports)
//            bottom half = Site Settings for whichever branch is selected in
//                        the centre panel (opening times, OCS code, patient
//                        app configuration)
//            Both halves render the SAME components as the Settings dialog
//            (OrgSettingsPanels.js), so the two can't drift apart.
//
// Two toggles from the real Settings dialog are deliberately absent: the
// monthly site performance review and the locum payment email. Both are stored
// behind endpoints scoped to the CALLER's token, so they cannot be read or
// written on another organisation's behalf without new backend routes.
//
// Backend endpoints (site_management.js):
//   GET  /api/sitemgmt/organisations
//   GET  /api/sitemgmt/tenants
//   POST /api/sitemgmt/organisations/create
//   POST /api/sitemgmt/organisations/members/add
//   POST /api/sitemgmt/organisations/members/remove
//   POST /api/sitemgmt/organisations/update
//   POST /api/sitemgmt/organisations/send-access-email

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  OrganisationIdentityPanel,
  OrganisationReportsPanel,
  BranchSettingsPanel,
  defaultOpeningTimes as DEFAULT_OPENING_TIMES,
} from "./OrgSettingsPanels";

const API_BASE = "https://api.pharmacegroup.co.uk";

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------
async function api(method, path, body) {
  const token = localStorage.getItem("authToken");
  const opts = { method, headers: { Authorization: `Bearer ${token}` } };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}${path}`, opts);
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch (_) { data = { error: text }; }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

// ---------------------------------------------------------------------------
// Style tokens — match HeadOffice.js / Config.js
// ---------------------------------------------------------------------------
const S = {
  // In normal flow, not fixed/inset — the nav rail must stay visible and the
  // view must sit in the content area like every other page. The height is
  // viewport-relative so the three panels get their own scrollbars rather than
  // pushing the whole page down.
  wrap:   { display: "flex", flexDirection: "column", fontFamily: "system-ui, sans-serif",
            height: "calc(100vh - 130px)", minHeight: 480 },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "0 0 10px",
            borderBottom: "1px solid #e5e7eb", background: "transparent", flexShrink: 0 },
  h1:     { fontSize: 20, fontWeight: 600, margin: 0 },
  sub:    { fontSize: 12, color: "#6b7280" },
  body:   { flex: 1, display: "grid",
            gridTemplateColumns: "minmax(300px, 1fr) minmax(230px, 0.75fr) minmax(460px, 1.9fr)",
            gap: 12, paddingTop: 12, overflow: "hidden", minHeight: 0 },
  panel:  { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6,
            display: "flex", flexDirection: "column", overflow: "hidden" },
  panelHead: { padding: "10px 12px", borderBottom: "1px solid #e5e7eb",
               fontSize: 14, fontWeight: 600, color: "#374151", background: "#f9fafb",
               display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  panelBody: { flex: 1, overflow: "auto" },
  input:  { width: "100%", boxSizing: "border-box", padding: "7px 10px", fontSize: 13,
            border: "1px solid #d1d5db", borderRadius: 4, fontFamily: "inherit" },
  btn:    { background: "#2563eb", color: "#fff", border: "none", borderRadius: 4,
            padding: "6px 12px", fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  btnGhost: { background: "#fff", color: "#374151", border: "1px solid #d1d5db",
              borderRadius: 4, padding: "6px 12px", fontSize: 13, cursor: "pointer",
              fontFamily: "inherit" },
  btnDanger: { background: "#fff", color: "#b91c1c", border: "1px solid #fca5a5",
               borderRadius: 4, padding: "6px 12px", fontSize: 13, cursor: "pointer",
               fontFamily: "inherit" },
  btnSmall: { fontSize: 11, padding: "3px 8px", borderRadius: 4, cursor: "pointer",
              border: "1px solid #d1d5db", background: "#fff", color: "#374151",
              fontFamily: "inherit", whiteSpace: "nowrap" },
  row:    { display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
            borderBottom: "1px solid #f3f4f6", cursor: "pointer" },
  kvKey:  { fontSize: 12, color: "#6b7280", fontWeight: 600 },
  kvVal:  { fontSize: 13, color: "#111827", wordBreak: "break-word" },
  empty:  { padding: 14, fontSize: 13, color: "#6b7280" },
  halfTitle: { fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase",
               letterSpacing: 0.4, margin: "0 0 10px" },
  error:  { margin: "8px 12px", padding: "8px 10px", background: "#fef2f2",
            border: "1px solid #fecaca", color: "#991b1b", fontSize: 12, borderRadius: 4 },
  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(17,24,39,0.45)",
                   zIndex: 1300, display: "flex", alignItems: "center", justifyContent: "center" },
  modal:  { background: "#fff", borderRadius: 8, width: "min(860px, 94vw)",
            maxHeight: "88vh", display: "flex", flexDirection: "column",
            boxShadow: "0 20px 50px rgba(0,0,0,0.25)" },
  modalHead: { padding: "14px 18px", borderBottom: "1px solid #e5e7eb",
               fontSize: 16, fontWeight: 600 },
  modalBody: { padding: 18, overflow: "auto", flex: 1 },
  modalFoot: { padding: "12px 18px", borderTop: "1px solid #e5e7eb",
               display: "flex", justifyContent: "flex-end", gap: 8 },
  label:  { display: "block", fontSize: 12, fontWeight: 600, color: "#374151",
            margin: "0 0 4px" },
};

const ROW_OK      = "#dcfce7";   // green  — complete
const ROW_OK_SEL  = "#bbf7d0";
const ROW_BAD     = "#fee2e2";   // light red — incomplete
const ROW_BAD_SEL = "#fecaca";

// An organisation is "complete" when it can actually be logged into and has
// something to look at: username + password + at least one member.
const isComplete = (o) =>
  !!(o && String(o.email || "").trim() && String(o.password || "").trim() &&
     Array.isArray(o.members) && o.members.length > 0);

const looksLikeEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

// Fields that have a real control somewhere in this view, so they're excluded
// from the read-only "Other fields" list.
const HANDLED_FIELDS = new Set([
  "_id", "organisation_name", "members", "email", "password",
  "manager_email",
  "report_sim", "report_expiring", "report_slow", "report_fridge",
  "send_expired", "send_timesheets", "timesheet_day", "payroll_month_end",
]);

// ---------------------------------------------------------------------------
// Right panel value rendering
// ---------------------------------------------------------------------------
function renderValue(v) {
  if (v === null || v === undefined || v === "") return <span style={{ color: "#9ca3af" }}>—</span>;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (v instanceof Date) return v.toLocaleDateString("en-GB");
  if (typeof v === "object") {
    // Mongo extended JSON dates arrive as { $date: "..." }
    if (v.$date) return new Date(v.$date).toLocaleDateString("en-GB");
    return (
      <pre style={{ margin: 0, fontSize: 11, background: "#f9fafb", border: "1px solid #f3f4f6",
                    borderRadius: 4, padding: 8, overflow: "auto", maxHeight: 220 }}>
        {JSON.stringify(v, null, 2)}
      </pre>
    );
  }
  return String(v);
}

// ---------------------------------------------------------------------------
// Create-organisation dialog
// ---------------------------------------------------------------------------
function CreateOrgDialog({ tenants, onCancel, onSave }) {
  const [name, setName]         = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [search, setSearch]     = useState("");
  const [chosen, setChosen]     = useState([]);   // [{ pharmacyName, dbName, email }]
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState("");

  const chosenDbNames = useMemo(() => new Set(chosen.map((c) => c.dbName)), [chosen]);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenants
      .filter((t) => !chosenDbNames.has(t.dbName))
      .filter((t) => !q ||
        String(t.pharmacyName || "").toLowerCase().includes(q) ||
        String(t.email || "").toLowerCase().includes(q))
      .sort((a, b) => String(a.pharmacyName || "").localeCompare(String(b.pharmacyName || ""),
        undefined, { sensitivity: "base" }));
  }, [tenants, search, chosenDbNames]);

  const save = async () => {
    setErr("");
    if (!name.trim())     { setErr("Enter an organisation name."); return; }
    if (!username.trim()) { setErr("Enter a username. Double-click a member's email address to use it."); return; }
    if (!password.trim()) { setErr("Enter a password."); return; }
    setBusy(true);
    try {
      await onSave({
        organisation_name: name.trim(),
        email: username.trim(),
        password: password.trim(),
        members: chosen.map((c) => ({
          pharmacyName: c.pharmacyName,
          pharmacyId: c.dbName,
          openingTimes: DEFAULT_OPENING_TIMES,
        })),
      });
    } catch (e) {
      setErr(e.message);
      setBusy(false);
    }
  };

  return (
    <div style={S.modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={S.modal}>
        <div style={S.modalHead}>Create new organisation</div>
        <div style={S.modalBody}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={S.label}>Organisation name</label>
              <input style={S.input} value={name} onChange={(e) => setName(e.target.value)}
                     placeholder="e.g. Crest Group" autoFocus />
            </div>
            <div>
              <label style={S.label}>Username</label>
              <input style={S.input} value={username} onChange={(e) => setUsername(e.target.value)}
                     placeholder="Double-click a member email, or type one" />
            </div>
            <div>
              <label style={S.label}>Password</label>
              <input style={S.input} value={password} onChange={(e) => setPassword(e.target.value)}
                     placeholder="Password" />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {/* Available pharmacies */}
            <div>
              <label style={S.label}>Pharmacies — click to add</label>
              <input style={{ ...S.input, marginBottom: 8 }} value={search}
                     onChange={(e) => setSearch(e.target.value)}
                     placeholder="Search pharmacies…" />
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 4, height: 300, overflow: "auto" }}>
                {available.map((t) => (
                  <div key={t.dbName}
                       onClick={() => setChosen((prev) => [...prev, t])}
                       style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", cursor: "pointer", fontSize: 13 }}>
                    <div style={{ fontWeight: 600 }}>{t.pharmacyName}</div>
                    {t.email && (
                      <div
                        onDoubleClick={(e) => { e.stopPropagation(); setUsername(t.email); }}
                        title="Double-click to use this address as the username"
                        style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline", cursor: "copy" }}>
                        {t.email}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>{t.dbName}</div>
                  </div>
                ))}
                {available.length === 0 && <div style={S.empty}>No pharmacies match that search.</div>}
              </div>
            </div>

            {/* Chosen members */}
            <div>
              <label style={S.label}>Members ({chosen.length}) — click to remove</label>
              <div style={{ border: "1px solid #e5e7eb", borderRadius: 4, height: 344, overflow: "auto" }}>
                {chosen.map((c) => (
                  <div key={c.dbName}
                       onClick={() => setChosen((prev) => prev.filter((x) => x.dbName !== c.dbName))}
                       style={{ padding: "7px 10px", borderBottom: "1px solid #f3f4f6", cursor: "pointer",
                                fontSize: 13, background: "#f0f9ff" }}>
                    <div style={{ fontWeight: 600 }}>{c.pharmacyName}</div>
                    {c.email && (
                      <div
                        onDoubleClick={(e) => { e.stopPropagation(); setUsername(c.email); }}
                        title="Double-click to use this address as the username"
                        style={{ fontSize: 11, color: "#2563eb", textDecoration: "underline", cursor: "copy" }}>
                        {c.email}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>{c.dbName}</div>
                  </div>
                ))}
                {chosen.length === 0 && <div style={S.empty}>No members yet. Click a pharmacy on the left.</div>}
              </div>
            </div>
          </div>

          {err && <div style={{ ...S.error, marginLeft: 0, marginRight: 0 }}>{err}</div>}
        </div>
        <div style={S.modalFoot}>
          <button style={S.btnGhost} onClick={onCancel} disabled={busy}>Cancel</button>
          <button style={S.btn} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-member dialog
// ---------------------------------------------------------------------------
function AddMemberDialog({ tenants, existingIds, onCancel, onAdd }) {
  const [search, setSearch] = useState("");
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenants
      .filter((t) => !existingIds.has(t.dbName))
      .filter((t) => !q || String(t.pharmacyName || "").toLowerCase().includes(q))
      .sort((a, b) => String(a.pharmacyName || "").localeCompare(String(b.pharmacyName || ""),
        undefined, { sensitivity: "base" }));
  }, [tenants, search, existingIds]);

  const add = async (t) => {
    setBusy(true); setErr("");
    try { await onAdd(t); } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <div style={S.modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ ...S.modal, width: "min(520px, 94vw)" }}>
        <div style={S.modalHead}>Add a pharmacy</div>
        <div style={S.modalBody}>
          <input style={{ ...S.input, marginBottom: 10 }} value={search} autoFocus
                 onChange={(e) => setSearch(e.target.value)} placeholder="Search pharmacies…" />
          <div style={{ border: "1px solid #e5e7eb", borderRadius: 4, height: 340, overflow: "auto" }}>
            {available.map((t) => (
              <div key={t.dbName} onClick={() => !busy && add(t)}
                   style={{ padding: "8px 10px", borderBottom: "1px solid #f3f4f6",
                            cursor: busy ? "wait" : "pointer", fontSize: 13 }}>
                <div style={{ fontWeight: 600 }}>{t.pharmacyName}</div>
                <div style={{ fontSize: 10, color: "#9ca3af" }}>{t.dbName}</div>
              </div>
            ))}
            {available.length === 0 && <div style={S.empty}>No pharmacies left to add.</div>}
          </div>
          {err && <div style={{ ...S.error, marginLeft: 0, marginRight: 0 }}>{err}</div>}
        </div>
        <div style={S.modalFoot}>
          <button style={S.btnGhost} onClick={onCancel}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function SiteManagement({ onClose }) {
  const [orgs, setOrgs]           = useState([]);
  const [tenants, setTenants]     = useState([]);
  const [selectedId, setSelected] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);   // pharmacyId
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [busy, setBusy]           = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAdd, setShowAdd]     = useState(false);
  const [sendingId, setSendingId] = useState(null);

  // Editing works on a DRAFT copy of the selected organisation, so a half-typed
  // change never leaks into the list and Revert is free. It is replaced whenever
  // the selection changes or the list is reloaded.
  const [draft, setDraft]       = useState(null);
  const [dirty, setDirty]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [o, t] = await Promise.all([
        api("GET", "/api/sitemgmt/organisations"),
        api("GET", "/api/sitemgmt/tenants"),
      ]);
      setOrgs(o.organisations || []);
      setTenants(t.tenants || []);
      return o.organisations || [];
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Alphabetical, then filtered on organisation name OR any member pharmacy name.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorted = [...orgs].sort((a, b) =>
      String(a.organisation_name || "").localeCompare(String(b.organisation_name || ""),
        undefined, { sensitivity: "base" }));
    if (!q) return sorted;
    return sorted.filter((o) =>
      String(o.organisation_name || "").toLowerCase().includes(q) ||
      (Array.isArray(o.members) && o.members.some((m) =>
        String(m?.pharmacyName || "").toLowerCase().includes(q))));
  }, [orgs, search]);

  const selected = useMemo(
    () => orgs.find((o) => String(o._id) === String(selectedId)) || null,
    [orgs, selectedId]
  );

  const members = useMemo(() => {
    const list = Array.isArray(selected?.members) ? [...selected.members] : [];
    return list.sort((a, b) =>
      String(a?.pharmacyName || "").localeCompare(String(b?.pharmacyName || ""),
        undefined, { sensitivity: "base" }));
  }, [selected]);

  const draftMember = useMemo(() => {
    if (!draft || !selectedMember) return null;
    return (draft.members || []).find((m) => String(m.pharmacyId) === String(selectedMember)) || null;
  }, [draft, selectedMember]);

  // Anything the Settings UI has no control for — holiday and overtime policy,
  // ringfence, version — shown read-only so it doesn't vanish from the admin view.
  const otherFields = useMemo(() => {
    if (!draft) return [];
    return Object.keys(draft)
      .filter((k) => !HANDLED_FIELDS.has(k))
      .sort((a, b) => a.localeCompare(b))
      .map((k) => [k, draft[k]]);
  }, [draft]);

  // Reset the draft whenever the selected organisation changes identity, or
  // when a reload brings back a different record. Deliberately keyed on the id
  // and not the object, so a reload doesn't silently discard edits in progress.
  useEffect(() => {
    const src = orgs.find((o) => String(o._id) === String(selectedId)) || null;
    setDraft(src ? JSON.parse(JSON.stringify(src)) : null);
    setDirty(false);
    setShowPassword(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  // Pull member add/remove (which save immediately) back into the draft without
  // throwing away unsaved field edits.
  useEffect(() => {
    if (!selected || !draft) return;
    setDraft((d) => (d ? { ...d, members: JSON.parse(JSON.stringify(selected.members || [])) } : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.members?.length]);

  const resetDraft = () => {
    setDraft(selected ? JSON.parse(JSON.stringify(selected)) : null);
    setDirty(false);
  };

  // Top-level organisation field.
  const patchOrg = (key, value) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  };

  // Field on the currently selected member. Matched on pharmacyId (the tenant
  // dbName), never on array position — the members panel renders them sorted.
  const patchMember = (key, value) => {
    setDraft((d) => {
      if (!d) return d;
      const members = (d.members || []).map((m) =>
        String(m.pharmacyId) === String(selectedMember) ? { ...m, [key]: value } : m
      );
      return { ...d, members };
    });
    setDirty(true);
  };

  const saveDraft = async () => {
    if (!draft) return;
    setSaving(true); setError("");
    try {
      await api("POST", "/api/sitemgmt/organisations/update", {
        organisationId: draft._id,
        patch: draft,
      });
      setDirty(false);
      // Resync from what the server actually stored — it clamps timesheet_day,
      // coerces the report flags to booleans and normalises members.
      const fresh = await load();
      if (Array.isArray(fresh)) {
        const saved = fresh.find((o) => String(o._id) === String(draft._id));
        if (saved) setDraft(JSON.parse(JSON.stringify(saved)));
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // -- actions ---------------------------------------------------------------
  const createOrg = async (payload) => {
    const res = await api("POST", "/api/sitemgmt/organisations/create", payload);
    await load();
    setSelected(res.organisationId || null);
    setShowCreate(false);
  };

  const addMember = async (tenant) => {
    if (!selected) return;
    await api("POST", "/api/sitemgmt/organisations/members/add", {
      organisationId: selected._id,
      pharmacyName: tenant.pharmacyName,
      pharmacyId: tenant.dbName,
    });
    await load();
  };

  const removeMember = async () => {
    if (!selected || !selectedMember) return;
    const m = members.find((x) => String(x.pharmacyId) === String(selectedMember));
    if (!window.confirm(`Remove ${m?.pharmacyName || selectedMember} from ${selected.organisation_name}?`)) return;
    setBusy(true);
    try {
      await api("POST", "/api/sitemgmt/organisations/members/remove", {
        organisationId: selected._id,
        pharmacyId: selectedMember,
      });
      setSelectedMember(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };

  const sendEmail = async (org, ev) => {
    ev.stopPropagation();
    let to = String(org.email || "").trim();
    if (!looksLikeEmail(to)) {
      const entered = window.prompt(
        `The username for "${org.organisation_name}" is "${to || "(not set)"}", which is not an email address.\n\n` +
        `Enter the address to send the access details to:`, "");
      if (entered === null) return;
      to = entered.trim();
      if (!looksLikeEmail(to)) { window.alert("That is not a valid email address."); return; }
    }
    if (!window.confirm(`Send platform access details for "${org.organisation_name}" to ${to}?`)) return;
    setSendingId(String(org._id));
    try {
      await api("POST", "/api/sitemgmt/organisations/send-access-email", {
        organisationId: org._id,
        to,
      });
      window.alert(`Sent to ${to}.`);
    } catch (e) {
      window.alert(`Could not send: ${e.message}`);
    } finally {
      setSendingId(null);
    }
  };

  // -- render ----------------------------------------------------------------
  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <h1 style={S.h1}>Site management</h1>
        <span style={S.sub}>
          {orgs.length} organisation{orgs.length === 1 ? "" : "s"}
          {search ? ` · ${visible.length} matching` : ""}
        </span>
        <div style={{ flex: 1 }} />
        <button style={S.btnGhost} onClick={load} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        {onClose && <button style={S.btnGhost} onClick={onClose}>Close</button>}
      </div>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.body}>
        {/* ---------------- LEFT: organisations ---------------- */}
        <div style={S.panel}>
          <div style={{ ...S.panelHead, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
            <button style={S.btn} onClick={() => setShowCreate(true)}>Create new organisation</button>
            <input style={S.input} value={search} onChange={(e) => setSearch(e.target.value)}
                   placeholder="Search organisation or pharmacy name…" />
          </div>
          <div style={S.panelBody}>
            {loading && <div style={S.empty}>Loading organisations…</div>}
            {!loading && visible.length === 0 && <div style={S.empty}>No organisations match that search.</div>}
            {visible.map((o) => {
              const sel = String(o._id) === String(selectedId);
              const ok  = isComplete(o);
              const bg  = ok ? (sel ? ROW_OK_SEL : ROW_OK) : (sel ? ROW_BAD_SEL : ROW_BAD);
              const missing = [
                String(o.email || "").trim() ? null : "username",
                String(o.password || "").trim() ? null : "password",
                (o.members || []).length ? null : "members",
              ].filter(Boolean);
              return (
                <div key={String(o._id)}
                     onClick={() => {
                       if (String(o._id) === String(selectedId)) return;
                       if (dirty && !window.confirm(
                         "There are unsaved changes to this organisation. Discard them?")) return;
                       setSelected(String(o._id));
                       setSelectedMember(null);
                     }}
                     style={{ ...S.row, background: bg,
                              boxShadow: sel ? "inset 3px 0 0 #1f2937" : "none" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                      {o.organisation_name || <em style={{ color: "#6b7280" }}>(no name)</em>}
                    </div>
                    <div style={{ fontSize: 11, color: "#4b5563" }}>
                      {(o.members || []).length} member{(o.members || []).length === 1 ? "" : "s"}
                      {o.email ? ` · ${o.email}` : ""}
                      {missing.length ? ` · missing ${missing.join(", ")}` : ""}
                    </div>
                  </div>
                  <button style={{ ...S.btnSmall, opacity: sendingId === String(o._id) ? 0.6 : 1 }}
                          disabled={sendingId === String(o._id)}
                          onClick={(e) => sendEmail(o, e)}
                          title="Email the platform access details to this organisation's username">
                    {sendingId === String(o._id) ? "Sending…" : "Send email"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------------- CENTRE: members ---------------- */}
        <div style={S.panel}>
          <div style={S.panelHead}>
            <span style={{ flex: 1 }}>
              Members{selected ? ` — ${selected.organisation_name}` : ""}
            </span>
            <button style={S.btnSmall} disabled={!selected} onClick={() => setShowAdd(true)}>Add</button>
            <button style={{ ...S.btnSmall, color: "#b91c1c", borderColor: "#fca5a5" }}
                    disabled={!selected || !selectedMember || busy}
                    onClick={removeMember}>Delete</button>
          </div>
          <div style={S.panelBody}>
            {!selected && <div style={S.empty}>Select an organisation on the left.</div>}
            {selected && members.length === 0 && <div style={S.empty}>This organisation has no members yet.</div>}
            {selected && members.map((m, i) => {
              const sel = String(m.pharmacyId) === String(selectedMember);
              return (
                <div key={`${m.pharmacyId || i}`}
                     onClick={() => setSelectedMember(String(m.pharmacyId))}
                     style={{ ...S.row, background: sel ? "#f0f9ff" : "transparent" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{m.pharmacyName || m.pharmacyId}</div>
                    <div style={{ fontSize: 10, color: "#9ca3af" }}>{m.pharmacyId}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------------- RIGHT: this organisation's own Settings ----------------
            Top half  = Settings -> Organisation Settings
            Bottom half = Settings -> Site Settings, for the selected branch
            Both halves scroll independently so neither can push the other off. */}
        <div style={S.panel}>
          <div style={S.panelHead}>
            <span style={{ flex: 1 }}>
              Settings{draft ? ` — ${draft.organisation_name || "(no name)"}` : ""}
            </span>
            {dirty && <span style={{ fontSize: 11, color: "#b45309", fontWeight: 600 }}>Unsaved changes</span>}
            <button style={S.btnSmall} disabled={!dirty || saving} onClick={resetDraft}>Revert</button>
            <button style={{ ...S.btn, padding: "4px 12px", fontSize: 12,
                             opacity: (!dirty || saving) ? 0.5 : 1 }}
                    disabled={!dirty || saving} onClick={saveDraft}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>

          {!draft && <div style={S.empty}>Select an organisation on the left.</div>}

          {draft && (
            <div style={{ flex: 1, minHeight: 0, display: "grid",
                          gridTemplateRows: "1fr 1fr", gap: 0 }}>

              {/* ---- TOP HALF: Organisation Settings ---- */}
              <div style={{ overflow: "auto", padding: 12, borderBottom: "2px solid #e5e7eb" }}>
                <div style={S.halfTitle}>Organisation settings</div>

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <OrganisationIdentityPanel
                    organisationName={draft.organisation_name || ""}
                    username={draft.email || ""}
                    password={draft.password || ""}
                    showPassword={showPassword}
                    onToggleShowPassword={setShowPassword}
                    onChange={patchOrg}
                    note={
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 12, lineHeight: 1.45 }}>
                        Changing the name, username or password signs this organisation's
                        users out — they'll need the new details to sign back in.
                      </div>
                    }
                  />

                  <OrganisationReportsPanel values={draft} onChange={patchOrg} />

                  <div style={{ fontSize: 11, color: "#6b7280", lineHeight: 1.5 }}>
                    The monthly site performance review and locum payment email toggles
                    aren't shown here: both live behind endpoints scoped to the signed-in
                    organisation, so they can only be set from that organisation's own
                    Settings dialog.
                  </div>

                  {/* Fields with no home in the Settings UI (holiday and overtime
                      policy, ringfence, version) stay visible read-only rather than
                      silently disappearing from the admin view. */}
                  {otherFields.length > 0 && (
                    <details style={{ fontSize: 12 }}>
                      <summary style={{ cursor: "pointer", color: "#374151", fontWeight: 600 }}>
                        Other fields on this record ({otherFields.length})
                      </summary>
                      <div style={{ padding: "10px 4px", display: "flex", flexDirection: "column", gap: 10 }}>
                        {otherFields.map(([k, v]) => (
                          <div key={k}>
                            <div style={S.kvKey}>{k}</div>
                            <div style={S.kvVal}>{renderValue(v)}</div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </div>
              </div>

              {/* ---- BOTTOM HALF: Site Settings for the selected branch ---- */}
              <div style={{ overflow: "auto", padding: 12 }}>
                <div style={S.halfTitle}>
                  Site settings
                  {draftMember
                    ? ` — ${draftMember.pharmacyName || draftMember.pharmacyId}`
                    : ""}
                </div>
                {!draftMember && (
                  <div style={S.empty}>Select a branch in the members panel to edit its settings.</div>
                )}
                {draftMember && (
                  <BranchSettingsPanel
                    member={draftMember}
                    onChange={patchMember}
                    onInheritPatientApp={() =>
                      window.alert("Inherit from organisation — API integration pending.")}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateOrgDialog tenants={tenants} onCancel={() => setShowCreate(false)} onSave={createOrg} />
      )}
      {showAdd && selected && (
        <AddMemberDialog
          tenants={tenants}
          existingIds={new Set((selected.members || []).map((m) => String(m.pharmacyId)))}
          onCancel={() => setShowAdd(false)}
          onAdd={async (t) => { await addMember(t); setShowAdd(false); }}
        />
      )}
    </div>
  );
}
